/* GFI Nexus — weekly cases-weighted price aggregation from REAL firm
   orders (fixed_pos / consignment_pos), for the AI Analyst Log's
   "Customer / GF / Grower" board.

   Replaces the 4 manual price-quote cards that used to live in
   AIAnalystLog.js (Blackberry Conventional/Organic own price, plus
   Mexico export-market price for both categories) — Arturo (2026-09-17):
   "no me gusta estar ingresando precios... por qué no mejor tomamos la
   información de las órdenes que ya me proporcionan los clientes" /
   "aqui no seria necesario ingresar precios de mexico ya" / "generar un
   solo campo que muestra el horizonte de 2 semanas y los productos
   unicamente que encuentre en ordenes firmes" (current + next ISO
   week — "firm orders" = POs already captured in Fixed/Consignment
   Pricing, never a projection or a typed-in quote).

   Two source shapes, kept separate because their pricing MODELS are
   genuinely different (not just field names):
   - Fixed Pricing (fixed_pos) — a real fixed triple per PO line:
     priceFS/priceGF/priceGrower (+ their *Real counterparts, set once
     a PO links to an invoice — same fallback rule FixedPricingRegistry.js
     already uses in calcProfits(): prefer Real when present and numeric,
     else fall back to the estimated value).
   - Consignment Pricing (consignment_pos) — Twin River-style RANGE
     pricing: priceEstTR/priceRealTR (the settled/estimated price paid
     by the client), commGFI/realCommGFI (GFI's own commission),
     differential/realDifferential (extra spread). Same Real-fallback
     idiom, different field names — this is Twin River's own model, not
     tied to that client by name (Arturo, 2026-09-17: "puede entrar en
     cualquier termino de pago fixed o consigment" — any future client
     could land in either pricing model).

   Both are labeled "Customer Price" in the UI (never "FS"/"TR" — those
   are just which underlying field happens to hold it), per Arturo's
   explicit correction the same day: this isn't Four-Seasons-only or
   Twin-River-only, any client can be fixed or consignment.

   Weight = cases (casesReceived || casesSent for Fixed; cases for
   Consignment) — never pallets, never a flat average. A product with
   zero real order lines in a given week simply never appears (no
   invented zeros, no placeholder rows) — same "never fabricate a data
   point" principle already applied elsewhere in this app (see
   fobPricesForecast.js's confidence-band clamp / holidayImpactCalc.js's
   real-vs-placeholder gate). */

/* Same ISO-8601 week algorithm FixedPricingRegistry.js/
   ConsignmentPricingRegistry.js already use to stamp `week` on save —
   duplicated here (not imported) per this repo's per-file convention,
   and recomputed from deliveryDate rather than trusted off the stored
   `week` field so a stale snapshot can never throw this off (same
   "always calculate, never trust a stored snapshot" principle used
   throughout this codebase, e.g. saleStatusCalc.js). Returns a bare
   week-of-year number (1-53), no year component — same simplification
   the stored `week` field itself already has, acceptable for a
   2-week current/next window (a rare year-boundary wrap is the only
   edge case, not worth complicating this for). */
export function getISOWeek(dateStr) {
  if (!dateStr) return null;
  var d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  var target = new Date(d.valueOf());
  var day = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - day + 3);
  var firstThu = new Date(target.getFullYear(), 0, 4);
  var diff = target - firstThu;
  return 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
}

/* [prevWeek, currentWeek, nextWeek] — 3-week window (Arturo, 2026-09-17:
   "agrega la semana anterior que si hay informacion y tambien la
   siguiente semana pero algo como orders pending"). Previous week is
   never force-shown — it simply won't produce any rows if it has no
   real firm orders (same "never fabricate a row" rule as the rest of
   this module). Next week is real data too (firm orders already
   captured for next week), just labeled "Pending Orders" in the UI
   since it hasn't shipped yet — not because it's estimated. */
export function getPrevCurrentNextWeeks(todayStr) {
  var today = todayStr || new Date().toISOString().slice(0, 10);
  var current = getISOWeek(today);
  var prevDate = new Date(today + "T00:00:00");
  prevDate.setDate(prevDate.getDate() - 7);
  var prev = getISOWeek(prevDate.toISOString().slice(0, 10));
  var nextDate = new Date(today + "T00:00:00");
  nextDate.setDate(nextDate.getDate() + 7);
  var next = getISOWeek(nextDate.toISOString().slice(0, 10));
  return [prev, current, next];
}

function numOrNull(v) {
  if (v === "" || v == null) return null;
  var n = parseFloat(v);
  return isNaN(n) ? null : n;
}

/* Real-if-present-else-estimated — same rule as FixedPricingRegistry.js's
   calcProfits() `pick()` / ConsignmentPricingRegistry.js's real/est
   fallback. */
function pickValue(row, realKey, estKey) {
  var real = numOrNull(row[realKey]);
  return real != null ? real : numOrNull(row[estKey]);
}

function productGroupKey(row) {
  return [row.productBase || row.productName || "", row.category || "", row.productName || ""].join("|");
}

function weightedAccumulate(acc, weight, value) {
  if (weight > 0 && value != null) {
    acc.sum += weight * value;
    acc.weight += weight;
  }
}

function weightedResult(acc) {
  return acc.weight > 0 ? acc.sum / acc.weight : null;
}

function extractFixedCustomerPoints(rows, weeks) {
  var points = [];
  (rows || []).forEach(function (row) {
    var week = getISOWeek(row.deliveryDate);
    if (week == null || weeks.indexOf(week) === -1) return;
    var cases = numOrNull(row.casesReceived) || numOrNull(row.casesSent) || 0;
    if (cases <= 0) return;
    var price = pickValue(row, "priceFSReal", "priceFS");
    if (price == null) return;
    points.push({
      week: week, productBase: row.productBase || row.productName || "", category: row.category || "", productName: row.productName || "", cases: cases,
      price: price, gfPrice: pickValue(row, "priceGFReal", "priceGF"), growerPrice: pickValue(row, "priceGrowerReal", "priceGrower"),
    });
  });
  return points;
}

/* Consignment (Twin River-style range) derives Customer/GF/Grower from
   the min/max band, same math as ConsignmentPricingRegistry.js's
   calcConsignment() — Arturo (2026-09-17): "consig tiene min max gf,
   esto es = a GF / min grower max grower esto es igual a grower / min
   tr max tr esto es igual a customer". Midpoint of the band is THE
   price used here (not priceEstTR/priceRealTR — this calc always uses
   the range midpoint, simpler and consistent with how Arturo described
   it), same commission/differential Real-fallback idiom as everywhere
   else in this module:
     midTR     = (minTR + maxTR) / 2                    → Customer Price
     midGF     = midTR × (1 − effCommTR%)                → GF Price
     midGrower = midGF − effDifferential                 → Grower Price */
var DEFAULT_COMM_TR = 10;
function consignmentDerivedPrices(row) {
  var minTR = numOrNull(row.minTR) || 0;
  var maxTR = numOrNull(row.maxTR) || 0;
  if (minTR <= 0 && maxTR <= 0) return null;
  var commTR = numOrNull(row.commTR);
  if (commTR == null) commTR = DEFAULT_COMM_TR;
  var effCommTR = pickValue(row, "realCommTR", "commTR");
  if (effCommTR == null) effCommTR = commTR;
  var diff = numOrNull(row.differential) || 0;
  var effDiff = pickValue(row, "realDifferential", "differential");
  if (effDiff == null) effDiff = diff;

  var midTR = (minTR + maxTR) / 2;
  var midGF = midTR * (1 - effCommTR / 100);
  var midGrower = midGF - effDiff;
  return { customerPrice: midTR, gfPrice: midGF, growerPrice: midGrower };
}

function extractConsignmentCustomerPoints(rows, weeks) {
  var points = [];
  (rows || []).forEach(function (row) {
    var week = getISOWeek(row.deliveryDate);
    if (week == null || weeks.indexOf(week) === -1) return;
    var cases = numOrNull(row.cases) || 0;
    if (cases <= 0) return;
    var derived = consignmentDerivedPrices(row);
    if (!derived) return;
    points.push({
      week: week, productBase: row.productBase || row.productName || "", category: row.category || "", productName: row.productName || "", cases: cases,
      price: derived.customerPrice, gfPrice: derived.gfPrice, growerPrice: derived.growerPrice,
    });
  });
  return points;
}

/* Summary board (Arturo, 2026-09-17: "al de fixed y consignment, un
   summary de las mismas 3 semanas haciendo el calculo con los numeros
   de ambos" — then, after seeing it only showed Customer Price: "la
   tarjeta summary fixed solo me da precio al customer, debe darme los
   mismos 3") — Customer/GF/Grower per week+product, pooling REAL order
   lines from both fixed_pos and consignment_pos together (cases-
   weighted across the two sources, not an average-of-averages — every
   individual line from both tables counts once, weighted by its own
   case count). Consignment's GF/Grower are the min/max-band-derived
   values from consignmentDerivedPrices — same 3 concepts as Fixed,
   different underlying fields. */
export function buildCombinedCustomerPrices(fixedPosRows, consignmentPosRows, weeks) {
  var points = extractFixedCustomerPoints(fixedPosRows, weeks).concat(extractConsignmentCustomerPoints(consignmentPosRows, weeks));
  var groups = {};
  points.forEach(function (p) {
    var key = p.week + "|" + [p.productBase, p.category, p.productName].join("|");
    if (!groups[key]) {
      groups[key] = {
        week: p.week, productBase: p.productBase, category: p.category, productName: p.productName, cases: 0,
        customer: { sum: 0, weight: 0 }, gf: { sum: 0, weight: 0 }, grower: { sum: 0, weight: 0 },
      };
    }
    var g = groups[key];
    g.cases += p.cases;
    weightedAccumulate(g.customer, p.cases, p.price);
    weightedAccumulate(g.gf, p.cases, p.gfPrice);
    weightedAccumulate(g.grower, p.cases, p.growerPrice);
  });
  return Object.keys(groups).map(function (k) {
    var g = groups[k];
    return {
      week: g.week, productBase: g.productBase, category: g.category, productName: g.productName, cases: g.cases,
      customerPrice: weightedResult(g.customer), gfPrice: weightedResult(g.gf), growerPrice: weightedResult(g.grower),
    };
  }).sort(function (a, b) { return a.week !== b.week ? a.week - b.week : (a.productBase + a.category).localeCompare(b.productBase + b.category); });
}

/* Fixed Pricing (any client on fixed-price terms) — one row per
   week+product with Customer/GF/Grower cases-weighted averages. */
export function buildWeightedFixedPrices(rows, weeks) {
  var groups = {};
  (rows || []).forEach(function (row) {
    var week = getISOWeek(row.deliveryDate);
    if (week == null || weeks.indexOf(week) === -1) return;
    var cases = numOrNull(row.casesReceived) || numOrNull(row.casesSent) || 0;
    if (cases <= 0) return;
    var key = week + "|" + productGroupKey(row);
    if (!groups[key]) {
      groups[key] = {
        week: week,
        productBase: row.productBase || row.productName || "",
        category: row.category || "",
        productName: row.productName || "",
        cases: 0,
        customer: { sum: 0, weight: 0 },
        gf: { sum: 0, weight: 0 },
        grower: { sum: 0, weight: 0 },
      };
    }
    var g = groups[key];
    g.cases += cases;
    weightedAccumulate(g.customer, cases, pickValue(row, "priceFSReal", "priceFS"));
    weightedAccumulate(g.gf, cases, pickValue(row, "priceGFReal", "priceGF"));
    weightedAccumulate(g.grower, cases, pickValue(row, "priceGrowerReal", "priceGrower"));
  });
  return Object.keys(groups).map(function (k) {
    var g = groups[k];
    return {
      week: g.week, productBase: g.productBase, category: g.category, productName: g.productName, cases: g.cases,
      customerPrice: weightedResult(g.customer), gfPrice: weightedResult(g.gf), growerPrice: weightedResult(g.grower),
    };
  }).sort(function (a, b) { return a.week !== b.week ? a.week - b.week : (a.productBase + a.category).localeCompare(b.productBase + b.category); });
}

/* Expands a builder's output so every product that has a firm order in
   AT LEAST ONE of the 3 weeks always shows exactly 3 rows — one per
   week, in week order — even when a given week has no real order for
   that product (Arturo, 2026-09-17: "siempre debe haber 3 renglones").
   A missing week is filled with nulls/zero cases (rendered as "—" by
   the UI), never a fabricated price — this only guarantees the ROW
   exists so the 3-week trend is always readable at a glance, it never
   invents a number. `extraNullFields` lists the source-specific price
   fields to null out (Fixed: gfPrice/growerPrice; Consignment:
   commissionPct/differential). */
export function fillWeekGaps(rows, weeks, extraNullFields) {
  var byProduct = {};
  var order = [];
  (rows || []).forEach(function (r) {
    var key = [r.productBase, r.category, r.productName].join("|");
    if (!byProduct[key]) { byProduct[key] = {}; order.push(key); }
    byProduct[key][r.week] = r;
  });
  var out = [];
  order.forEach(function (key) {
    var parts = key.split("|");
    weeks.forEach(function (w) {
      var existing = byProduct[key][w];
      if (existing) { out.push(existing); return; }
      var filled = { week: w, productBase: parts[0], category: parts[1], productName: parts[2], cases: 0, customerPrice: null };
      (extraNullFields || []).forEach(function (f) { filled[f] = null; });
      out.push(filled);
    });
  });
  return out;
}

/* Consignment Pricing (any client on range/consignment terms) — one row
   per week+product with cases-weighted Customer/GF/Grower, each derived
   from the min/max band midpoint (see consignmentDerivedPrices above) —
   same 3-column shape as Fixed Pricing, per Arturo's 2026-09-17
   correction (this used to show Commission%/Differential instead). */
export function buildWeightedConsignmentPrices(rows, weeks) {
  var groups = {};
  (rows || []).forEach(function (row) {
    var week = getISOWeek(row.deliveryDate);
    if (week == null || weeks.indexOf(week) === -1) return;
    var cases = numOrNull(row.cases) || 0;
    if (cases <= 0) return;
    var derived = consignmentDerivedPrices(row);
    if (!derived) return;
    var key = week + "|" + productGroupKey(row);
    if (!groups[key]) {
      groups[key] = {
        week: week,
        productBase: row.productBase || row.productName || "",
        category: row.category || "",
        productName: row.productName || "",
        cases: 0,
        customer: { sum: 0, weight: 0 },
        gf: { sum: 0, weight: 0 },
        grower: { sum: 0, weight: 0 },
      };
    }
    var g = groups[key];
    g.cases += cases;
    weightedAccumulate(g.customer, cases, derived.customerPrice);
    weightedAccumulate(g.gf, cases, derived.gfPrice);
    weightedAccumulate(g.grower, cases, derived.growerPrice);
  });
  return Object.keys(groups).map(function (k) {
    var g = groups[k];
    return {
      week: g.week, productBase: g.productBase, category: g.category, productName: g.productName, cases: g.cases,
      customerPrice: weightedResult(g.customer), gfPrice: weightedResult(g.gf), growerPrice: weightedResult(g.grower),
    };
  }).sort(function (a, b) { return a.week !== b.week ? a.week - b.week : (a.productBase + a.category).localeCompare(b.productBase + b.category); });
}
